
import React from 'react';
import {RADIO_SINGLE_RANDOM_VALUE, RadioButton} from "../prm/radio-button";
import { Name } from "../prm/name";
import { Text, dobValidator, mmddyyyyValidator } from "../prm/text";
import { FlowRender } from "../flow-render";
import {role_titles} from "../onboarding-utils";
import { IdentificationDocumentsMenu } from './identification-documents-menu';
import { CountryOptionMenu } from '../prm/country-menu';
import { StateOptionMenu } from '../prm/state-menu';
import {InvestmentAccountType} from "../proto/investment_accounts";
import {GreyBox} from "../../client/funcs/utils";
import {DwollaCustomerStates} from "../dwolla/constants";
import {init_state, isFilledElseHighlight, UploadFiles} from "../onboarding-shared/widgets/uploader";
import {uploadCustomerDocuments} from "../../client/superadmin/payments-reducer";

const title = "We are required to collect this information";
const document_type_driver_license = "DriverLicense";
const document_type_passport = "Passport";

const PREFIX_0 = "Identification_0";
const PREFIX_1 = "Identification_1";

const Identification_1_disable = new RadioButton(
  new Name("Identification_1_disable"),
[ { id: "disable", label: "Disable" } ]);
Identification_1_disable.do_checkbox = true;

const DOCUMENT_PREFIX = "dwolla_document";

class Identification extends FlowRender {

  constructor(index) {
    super(index, []);

    this.create_params(PREFIX_0);
    this.create_params(PREFIX_1);
    this.prmList.push(Identification_1_disable);
    Identification_1_disable.setCallback(this.onChange);

    this.url = "identification";
  }

  onMount(component) {
    component.props.refreshDwolla(component.getOnboardingIndex(), component.props.username);
  }

  create_params = page_prefix => {
    this[page_prefix] = {};

    // usa country checkbox
    this[page_prefix].usaCountry_checkbox = new RadioButton(
      new Name(page_prefix + "_country_checkbox"),
      [
        new Name("usa"),
      ], [], RADIO_SINGLE_RANDOM_VALUE);
    this[page_prefix].usaCountry_checkbox.setCallback(this.onChange);
    this[page_prefix].usaCountry_checkbox.do_checkbox = true;
    this.prmList.push(this[page_prefix].usaCountry_checkbox);

    // citizenship country prm
    this[page_prefix].identification_citizenship_country = new CountryOptionMenu(new Name(page_prefix + "_Citizenship_Country", "Citizenship_Country"));
    this.prmList.push(this[page_prefix].identification_citizenship_country)

    // identification document prm (driver license/passport)
    const identification_document = new IdentificationDocumentsMenu(new Name(page_prefix + "_Document", "Document"));
    this.prmList.push(identification_document);
    this[page_prefix].identification_document = identification_document;

    // passport country prm
    const identification_passport_country = new CountryOptionMenu(new Name(page_prefix + "_Passport_Country", "Passport_Country"));
    this.prmList.push(identification_passport_country)
    this[page_prefix].identification_passport_country = identification_passport_country;

    // driver license state prm
    const identification_driver_license_state = new StateOptionMenu(new Name(page_prefix + "_Driver_License_State", "Driver_License_State"));
    identification_driver_license_state.fake = true;
    this.prmList.push(identification_driver_license_state)
    this[page_prefix].identification_driver_license_state = identification_driver_license_state;

    // issue date prm
    const document_issue_date = new Text(
      new Name(page_prefix + "_Document_Issue_Date", "Document_Issue_Date"),
      null, { path: "date.past", arg0: 18 });
    document_issue_date.onAction = this.onTextEdit;
    document_issue_date.hint = "MM/DD/YYYY";
    document_issue_date.validator = dobValidator;
    document_issue_date.blurValidator = mmddyyyyValidator;
    this.prmList.push(document_issue_date);
    this[page_prefix].document_issue_date = document_issue_date;

    // expiration date prm
    const document_expiration_date = new Text(
      new Name(page_prefix + "_Document_Expiration_Date", "Document_Expiration_Date"),
    null, { path: "date.future", arg0: 5 });
    document_expiration_date.onAction = this.onTextEdit;
    document_expiration_date.hint = "MM/DD/YYYY";
    document_expiration_date.validator = dobValidator;
    document_expiration_date.blurValidator = mmddyyyyValidator;
    this.prmList.push(document_expiration_date);
    this[page_prefix].document_expiration_date = document_expiration_date;

    // identification number prm
    const identification_passport_number =
      new Text(new Name(page_prefix + "_Passport_Identification_Number", "Passport_Identification_Number"),
        null, {
          path: "finance.accountTemplate",
          arg0: "#########"
        });
    identification_passport_number.onAction = this.onTextEdit;
    this.prmList.push(identification_passport_number);
    this[page_prefix].identification_passport_number = identification_passport_number;
  };

  onChange = (component, newState) => {
    [PREFIX_0, PREFIX_1].forEach(prefix => {
      if (this[prefix].usaCountry_checkbox.isClicked(newState)) {
        this[prefix].identification_citizenship_country.setValue(newState, 0);
      }
      // citizenship country dropdown
      const citizenshipCountryDropdown = this[prefix].identification_citizenship_country;
      citizenshipCountryDropdown.setDisabled(newState, this[prefix].usaCountry_checkbox.isSelected(newState, 0));
    });

    const is_disabled_clicked = Identification_1_disable.isItemClicked(0, newState);
    const is_usa_checkbox_clicked = this.Identification_1.usaCountry_checkbox.isClicked(newState);
    // handle the entire right column
    Object.values(this.Identification_1).forEach(prm => {
      if (prm.name.id === "Identification_1_Citizenship_Country") {
        prm.setDisabled(newState, is_disabled_clicked || is_usa_checkbox_clicked);
      } else {
        prm.setDisabled(newState, is_disabled_clicked);
      }
    })

  }

  /** Called after a text entry is finished editing. */
  onTextEdit = component => {
    const newState = Object.assign({}, component.state);
    component.setState(newState);
  };

  init(componentState, appState) {
    super.init(componentState, appState);
    componentState.loading = false;
    init_state(DOCUMENT_PREFIX, appState, componentState);
  }

  canNavigateNext(component) {
    const highlight_rects = [];

    this.prmList.forEach((prm) => {
      if (prm.type === "Text" && !prm.getDisabled(component.state)) {
        prm.isFilledElseHighlight(component.state, highlight_rects);
      }
    });

    isFilledElseHighlight(DOCUMENT_PREFIX, component, highlight_rects);

    if (highlight_rects.length > 0) {
      component.setState({ highlight_rects });
      return null;
    }

    /*if (is_ira_rollover(component.getOnboardingData())) {
      return component.getRootUrl() + "/roll_over_attestation";
    }*/

    return component.getRootUrl() + "/trusted_contact";
  }

  renderCountry = (component, page_prefix) => {
    const input_style = {
      width: "100%",
    };
    // const checkbox_style = {
    //   transform: "scale(1.2) translate(0, 0.2rem)",
    // };
    const contents_style = {
      paddingTop: "1rem",
      alignItems: "center",
      justifyContent: 'space-between'
    }

    const checkBoxLabel = (
        <div>
              <span>USA</span>
        </div>
    )
    

    const citizenshipCountryDropdown = this[page_prefix].identification_citizenship_country;
    return (
      <div>
        <div>Country of Citizenship</div>
        <div style={contents_style}>
          <div style={{ width: 'fit-content' }}>
            {this[page_prefix].usaCountry_checkbox.renderCheckboxWithInfo(0, component, checkBoxLabel)}
          </div>
          <div style={{height: "0.5rem"}}/>
          <div>
            {citizenshipCountryDropdown.renderMenu(component, input_style, "CitizenshipCountry")}
          </div>
        </div>
      </div>
    )
  }

  onFinish(component) {
    const dwolla = component.getDwollaData(0);
    const is_document_mode = dwolla && dwolla.customer
      && dwolla.customer.state === DwollaCustomerStates.DOCUMENT;
    const onboarding = component.getOnboardingData();
    const type_prm = this[PREFIX_0].identification_document;
    const doc_type = type_prm.getValue(component.state).id;
    // Matches @ca/dwolla DocumentType
    const dwolla_document_type = doc_type === "DriverLicense"
      ? "license" : "passport";

    if (is_document_mode && DOCUMENT_PREFIX in onboarding) {
      // If user is still in document mode, and a document is in onboarding then upload to Dwolla
      uploadCustomerDocuments(
        component.props.token, component.getOnboardingIndex(), dwolla_document_type)
        .then();
    }

  }

  maybeRenderUploader = component => {
    const dwolla = component.getDwollaData(0);
    const is_document_mode = dwolla && dwolla.customer
      && dwolla.customer.state === DwollaCustomerStates.DOCUMENT;

    if (!is_document_mode) {
      return null;
    }

    const title_style = {
      fontSize: "1.5rem",
    }

    const type_prm = this[PREFIX_0].identification_document;
    const doc_type = type_prm.getValue(component.state).id;
    const title_text = doc_type === "DriverLicense"
      ? "Upload a scan of your Driver's License"
      : "Upload a scan of your Passport";

    return (
      <div style={{marginTop: "2rem"}}>
        <div style={title_style}>
          {title_text}
        </div>
        <GreyBox>
          <UploadFiles
            prefix={"dwolla_document"}
            onboarding={component.getOnboardingData()}
            component={component}
            max_files={1}/>
        </GreyBox>
      </div>
    )
  }

  renderColumn = (component, page_prefix, title, do_disable, total_columns) => {
    const input_style = {
      width: "100%",
    };
    const dropdown_style = {
      width: "100%",
    };
    const textbox_style = {
      width: "50%",
    }

    const canRenderDriverLicenseFields = () => {
      return this[page_prefix].identification_document.getValue(component.state).id === document_type_driver_license;
    };
  
    const canRenderPassportFields = () => {
      return this[page_prefix].identification_document.getValue(component.state).id === document_type_passport;
    };

    const title_div = title
      ? (<div align={"center"}>{title}</div>)
      : null;
    const contanier_style = {
      paddingTop: "1rem",
      width: "40rem",
    }

    let disable_div = null;

    if (total_columns === 2) {
      disable_div = do_disable
        ? (
          <div style={{ display: "flex", alignItems: "center", marginBottom: "1rem"}}>
            <div>{ Identification_1_disable.renderCheckbox(0, component) }</div>
            <div style={{width: "1rem"}}/>
            <div>Check if minor has no identification</div>
          </div>
        ) : (<div style={{height: "2.5rem"}}/>);
    }

    return (
      <div key={page_prefix} style={contanier_style}>
        { title_div }
        <GreyBox>
          { disable_div }
          { this.renderCountry(component, page_prefix) }
          <div style={{paddingTop: "2rem"}}>
            <div>
              Document
            </div>
            {this[page_prefix].identification_document.renderMenu(component, dropdown_style, "Document")}
          </div>
          {canRenderDriverLicenseFields() &&
            <div style={{paddingTop: "2rem"}}>
              Driver License's State
              <div>
                {this[page_prefix].identification_driver_license_state.renderMenu(component, dropdown_style, "DriverLicenseState")}
              </div>
            </div>
          }
          {canRenderPassportFields() &&
            <div>
              <div style={{paddingTop: "2rem"}}>
                Passport's Country
                <div>
                  {this[page_prefix].identification_passport_country.renderMenu(component, input_style, "PassportCountry")}
                </div>
              </div>
            </div>
          }
          <div style={{paddingTop: "2rem"}}>
            { canRenderPassportFields() ? "Passport ID": "Driver's License ID" }
            <div>
              {this[page_prefix].identification_passport_number.renderInputDiv(component, textbox_style)}
            </div>
          </div>
          <div style={{paddingTop: "2rem"}}>
            Issue Date
            <div>
              {this[page_prefix].document_issue_date.renderInputDiv(component, textbox_style)}
            </div>
          </div>
          <div style={{paddingTop: "2rem"}}>
            Expiration Date
            <div>
              {this[page_prefix].document_expiration_date.renderInputDiv(component, textbox_style)}
            </div>
          </div>
        </GreyBox>
      </div>
    )
  }

  render = component => {

    const container_style = {
      margin: "auto",
      width: "50%"
    };
    const onboarding = component.getOnboardingData();
    const is_custodial_ira = onboarding.account_type === InvestmentAccountType.custodial_ira.id;
    const titles = role_titles(onboarding);

    let columns;

    if (is_custodial_ira) {
      columns = [
        (<div key={"st"} style={{flex: "1"}}/>),
        this.renderColumn(component, PREFIX_0, titles[0], false, 2),
        this.renderColumn(component, PREFIX_1, titles[1], true, 2),
        (<div key={"en"} style={{flex: "1"}}/>),
      ]
    } else {
      columns = [
        (<div key={"st"} style={{flex: "1"}}/>),
        this.renderColumn(component, PREFIX_0, null, false, 1),
        (<div key={"en"} style={{flex: "1"}}/>),
      ]
    }


    return (
      <div>
      {this.renderHighlightRects(component)}
        {this.renderTitle(component, "Identification", "50%", "952149824")}
        {this.renderProgress(component)}
        <section style={{paddingTop: "1rem"}} className={"section"}>
          <div className="divgrid-container-responsive" style={container_style}>
            <div style={{fontSize: "1.5rem"}}>
              {title}
            </div>
          </div>
          <div style={{display: "flex", gap: "1rem"}}>
            {columns}
          </div>
          <div className="divgrid-container-responsive" style={container_style}>
            {this.maybeRenderUploader(component)}
          </div>
        </section>
        {this.renderNextButtonRow(component, true)}
      </div>
  );
  }
  }

  export { Identification };
